import { type StudyMissionSelectDto, type StudySelectDto } from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { successToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import { useGetMeiaKopTemplates, useGetMeiaStudyMissionThreads, useUpdateMeiaStudy } from '@queries/meia.query';
import { type MeiaStudyKopFormValues, meiaStudyKopSchema } from '@schemas/meia.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import { Layers3Icon } from 'lucide-react';
import { type FC, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';
import { getMeiaMissionCode, sortMeiaMissionsByCreatedAt } from './meia-mission-display.util';
import { STUDY_ADVERSARY_PROFILE_OPTIONS, STUDY_TIME_HORIZON_OPTIONS } from './meia-study-options.util';
import { MeiaTemplatePopover } from './meia-template-popover.component';

type MeiaKopEditorTileProps = {
  missions: StudyMissionSelectDto[];
  study: StudySelectDto;
};

export const MeiaKopEditorTile: FC<MeiaKopEditorTileProps> = (props) => {
  const { missions, study } = props;
  const [searchParams] = useSearchParams();
  const { data: kopTemplates } = useGetMeiaKopTemplates();
  const { isPending: isSaving, mutateAsync: updateStudy } = useUpdateMeiaStudy();

  const orderedMissions = useMemo(() => sortMeiaMissionsByCreatedAt(missions), [missions]);
  const { threads } = useGetMeiaStudyMissionThreads({ missions: orderedMissions, studyId: study.id });

  const requestedMissionId = searchParams.get('missionId');
  const activeMissionId = orderedMissions.find((mission) => mission.id === requestedMissionId)
    ? requestedMissionId
    : orderedMissions[0]?.id;

  const defaultValues: MeiaStudyKopFormValues = {
    adversaryProfiles: study.adversaryProfiles ?? [],
    keyOperationalProblem: study.keyOperationalProblem ?? '',
    timeHorizon: study.timeHorizon ?? null,
  };

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      await updateStudy({
        adversaryProfiles: value.adversaryProfiles,
        keyOperationalProblem: value.keyOperationalProblem.trim() ? value.keyOperationalProblem : null,
        studyId: study.id,
        timeHorizon: value.timeHorizon,
      });
      // Rebase the form on the saved values so it is no longer dirty and the Save/Discard footer hides.
      form.reset(value);
      successToast({ description: 'Key Operational Problem saved.' });
    },
    onSubmitInvalid: createFormInvalidHandler<MeiaStudyKopFormValues>(),
    validators: { onSubmit: meiaStudyKopSchema },
  });

  const resolvedTemplates = kopTemplates?.kopTemplates ?? [];

  return (
    <Tile
      contentClassName="gap-4"
      header={{
        actions: (
          <MeiaTemplatePopover
            onSelect={(template) => form.setFieldValue('keyOperationalProblem', template.description ?? template.name)}
            templates={resolvedTemplates.map((template) => ({
              description: template.description,
              id: template.id,
              name: template.name,
            }))}
          />
        ),
        title: (
          <span className="inline-flex items-center gap-1.5">
            <span>Key Operational Problem</span>
            <TooltipComponent side="top">
              One Key Operational Problem can have many missions; each mission owns its own threads downstream.
            </TooltipComponent>
          </span>
        ),
      }}
    >
      <form
        {...MEIA_AUTOFILL_IGNORE_PROPS}
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.AppForm>
          <form.AppField name="keyOperationalProblem">
            {(field) => (
              <field.TextAreaField
                description="This is the anchor for every mission below. All mission DMOs should be plausibly reducible to making progress on this problem."
                label="Problem Statement"
                placeholder="State the operational problem the study must solve. Keep it outcome-framed — 'how do we…' or 'what must we prevent…' — not a list of tasks."
                required
                rows={4}
              />
            )}
          </form.AppField>

          <div className="flex flex-wrap items-start gap-3 text-[11px]">
            <span className="flex items-center gap-1.5 text-gray-500">
              <Layers3Icon className="size-3" /> Missions under this problem:
            </span>
            <span className="flex flex-wrap items-center gap-1.5">
              {orderedMissions.map((mission, index) => (
                <span
                  key={mission.id}
                  className={cn(
                    'rounded-sm px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider',
                    mission.id === activeMissionId ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600',
                  )}
                >
                  {getMeiaMissionCode({ index, mission })}
                </span>
              ))}
              <span className="text-gray-400">
                ({orderedMissions.length} total · {threads.length} {threads.length === 1 ? 'thread' : 'threads'} across
                all missions)
              </span>
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <form.AppField name="adversaryProfiles">
              {(field) => (
                <field.SelectMultipleField
                  label="Adversary Profile"
                  options={STUDY_ADVERSARY_PROFILE_OPTIONS}
                  placeholder="Select adversary profiles"
                />
              )}
            </form.AppField>
            <form.AppField name="timeHorizon">
              {(field) => (
                <field.SelectField
                  clearable
                  label="Time Horizon"
                  options={STUDY_TIME_HORIZON_OPTIONS}
                  placeholder="Select time horizon"
                />
              )}
            </form.AppField>
          </div>

          <form.Subscribe selector={(state) => state.isDirty}>
            {(isDirty) =>
              isDirty ? (
                <div className="flex justify-end gap-2 pt-1">
                  <Button
                    disabled={isSaving}
                    onClick={() => form.reset()}
                    type="button"
                    variant="outline"
                  >
                    Discard
                  </Button>
                  <Button
                    disabled={isSaving}
                    type="submit"
                  >
                    {isSaving ? 'Saving...' : 'Save Problem'}
                  </Button>
                </div>
              ) : null
            }
          </form.Subscribe>
        </form.AppForm>
      </form>
    </Tile>
  );
};
